import { ChangeEvent, ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { FiPaperclip, FiSend, FiX } from 'react-icons/fi'

const MAX_FILE_MB = 10

type PendingFile = {
  file: File
  /** objectURL para pré-visualizar a imagem antes de subir. */
  preview: string | null
}

type MessageComposerProps = {
  disabled?: boolean
  isSending?: boolean
  placeholder?: string
  onSend: (content: string, files: File[]) => Promise<void> | void
}

/**
 * Caixa de escrita do paciente.
 *
 * Ela não sabe nada de Firebase nem de API: só junta texto e arquivos e entrega
 * ao `onSend`. Quem decide o que fazer com isso é o painel.
 */
const MessageComposer = ({ disabled, isSending, placeholder, onSend }: MessageComposerProps) => {
  const [content, setContent] = useState('')
  const [files, setFiles] = useState<PendingFile[]>([])
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // objectURL é um recurso do navegador: sem revoke, vaza memória a cada anexo.
  useEffect(
    () => () => {
      files.forEach((item) => item.preview && URL.revokeObjectURL(item.preview))
    },
    [files]
  )

  /** Anexa o que couber no limite e avisa sobre o que não coube. */
  const attach = (selected: File[]) => {
    if (selected.length === 0) return

    const tooBig = selected.filter((file) => file.size > MAX_FILE_MB * 1024 * 1024)

    setError(tooBig.length > 0 ? `Arquivo acima de ${MAX_FILE_MB}MB não pode ser enviado.` : null)

    const accepted = selected
      .filter((file) => file.size <= MAX_FILE_MB * 1024 * 1024)
      .map((file) => ({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }))

    setFiles((current) => [...current, ...accepted])
  }

  const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
    attach(Array.from(event.target.files ?? []))

    // permite escolher o mesmo arquivo de novo depois de remover
    if (inputRef.current) inputRef.current.value = ''
  }

  /**
   * Colar imagem (print de tela, foto copiada) direto no campo.
   *
   * Um print colado chega no clipboard como arquivo sem nome; o nome importa
   * porque vira parte da chave no S3, então damos um com a hora da captura.
   */
  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const images = Array.from(event.clipboardData?.items ?? [])
      .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter((file): file is File => !!file)
      .map((file) =>
        file.name && file.name !== 'image.png'
          ? file
          : new File([file], `print-${Date.now()}.${file.type.split('/')[1] || 'png'}`, { type: file.type })
      )

    if (images.length === 0) return

    // Sem isto o navegador também colaria o caminho do arquivo como texto.
    event.preventDefault()
    attach(images)
  }

  const removeFile = (index: number) => {
    setFiles((current) => {
      const item = current[index]
      if (item?.preview) URL.revokeObjectURL(item.preview)

      return current.filter((_, position) => position !== index)
    })
  }

  const canSend = !disabled && !isSending && (content.trim().length > 0 || files.length > 0)

  const submit = async () => {
    if (!canSend) return

    const pending = files.map((item) => item.file)
    const text = content.trim()

    // Limpa antes de esperar a rede: a mensagem já volta pela escuta do RTDB e
    // o campo travado dando a impressão de que não enviou é pior que o otimismo.
    setFiles((current) => {
      current.forEach((item) => item.preview && URL.revokeObjectURL(item.preview))

      return []
    })
    setContent('')

    await onSend(text, pending)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter envia, Shift+Enter quebra linha — como em qualquer chat.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <div className="border-t border-neutral-200 bg-white p-3">
      {files.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {files.map((item, index) => (
            <div
              key={`${item.file.name}-${item.file.lastModified}`}
              className="relative flex items-center gap-2 rounded-lg border border-solid border-neutral-200 bg-neutral-50 p-1.5">
              {item.preview ? (
                <img src={item.preview} alt="" className="h-10 w-10 rounded object-cover" />
              ) : (
                <span className="max-w-[140px] truncate px-1 text-xs text-neutral-700">{item.file.name}</span>
              )}

              <button
                type="button"
                onClick={() => removeFile(index)}
                aria-label="Remover anexo"
                className="rounded-full border-0 bg-neutral-200 p-1 text-neutral-600 hover:bg-neutral-300">
                <FiX size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="mb-2 text-xs text-red-600">{error}</p>}

      <div className="flex items-end gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          aria-label="Anexar arquivo"
          title={`Anexar arquivo (até ${MAX_FILE_MB}MB)`}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-0 bg-transparent text-neutral-500 hover:bg-neutral-100 disabled:opacity-40">
          <FiPaperclip size={18} />
        </button>

        <input ref={inputRef} type="file" multiple hidden accept="image/*,application/pdf" onChange={handleFiles} />

        <textarea
          rows={1}
          value={content}
          disabled={disabled}
          onChange={(event) => setContent(event.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder ?? 'Escreva sua mensagem…'}
          className="max-h-32 min-h-[40px] flex-1 resize-none rounded-2xl border border-solid border-neutral-300 bg-neutral-50 px-4 py-2 font-sans text-sm outline-none transition focus:border-primary-700 focus:bg-white disabled:bg-neutral-50"
        />

        <button
          type="button"
          disabled={!canSend}
          onClick={submit}
          aria-label="Enviar"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-0 bg-primary-800 text-white transition hover:bg-primary-900 disabled:cursor-not-allowed disabled:opacity-40">
          {/* A seta do Feather tem a ponta deslocada para a direita: 1px para a
              esquerda a faz parecer centrada no círculo. */}
          <FiSend size={16} className="-translate-x-px" />
        </button>
      </div>
    </div>
  )
}

export default MessageComposer
